import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, 
  DollarSign, 
  Calendar, 
  Plus, 
  FileText, 
  CreditCard,
  AlertTriangle,
  CheckCircle2,
  Clock,
  User,
  Phone,
  Download,
  Loader2,
  Pencil,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import html2pdf from 'html2pdf.js';
import { logger } from '../utils/logger';
import { formatLocalDate, formatLocalDateLong } from '../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// V1 ALLOWED CATEGORIES - Adjustment, Discount, Refund disabled until V2
const PAYMENT_CATEGORIES = [
  'Booking Money',
  'Down Payment',
  'Installment',
  'Partial',
  'Full-Payment',
  'One-time'
];

const PAYMENT_METHODS = ['Cash', 'Bank', 'Card', 'Zelle', 'Other'];

export default function ContractDetailPage() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [summary, setSummary] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const [futureAdjustments, setFutureAdjustments] = useState([]);
  const [showEditPayment, setShowEditPayment] = useState(false);
  const [editPaymentForm, setEditPaymentForm] = useState({});
  const [editingPayment, setEditingPayment] = useState(false);
  const { moduleAction, role } = usePermissions();
  const canEditContracts = role === 'owner' || moduleAction('contracts', 'edit');
  const canEditPayments = role === 'owner' || moduleAction('payments', 'edit'); // Track which invoice is being generated
  const canDelete = role === 'owner';
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_category: '',
    payment_method: '',
    money_receipt_no: '',
    notes: '',
    applied_installment_id: 'auto'
  });

  const fetchContract = async () => {
    try {
      const [contractRes, summaryRes, installmentsRes, paymentsRes, faRes] = await Promise.all([
        fetch(`${API_URL}/api/financial/contracts/${contractId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/financial/contracts/${contractId}/summary`, { credentials: 'include' }),
        fetch(`${API_URL}/api/financial/contracts/${contractId}/installments`, { credentials: 'include' }),
        fetch(`${API_URL}/api/financial/contracts/${contractId}/payments`, { credentials: 'include' }),
        fetch(`${API_URL}/api/financial/contracts/${contractId}/future-adjustments`, { credentials: 'include' })
      ]);

      if (contractRes.ok) {
        const contractData = await contractRes.json();
        setContract(contractData);
      }

      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }

      if (installmentsRes.ok) {
        const installmentsData = await installmentsRes.json();
        setInstallments(installmentsData);
      }

      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json();
        setPayments(paymentsData);
      }

      if (faRes.ok) {
        const faData = await faRes.json();
        setFutureAdjustments(faData);
      }
    } catch (error) {
      logger.error('Error fetching contract:', error);
      toast.error('Error loading contract details');
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchContract();
  }, [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditContract = () => {
    navigate(`/finance/contracts?edit=${contractId}`);
  };

  const openEditPayment = (payment) => {
    setEditPaymentForm({
      payment_id: payment.payment_id,
      payment_date: payment.payment_date ? payment.payment_date.split('T')[0] : '',
      amount: payment.amount || 0,
      payment_category: payment.payment_category || '',
      payment_method: payment.payment_method || '',
      money_receipt_no: payment.money_receipt_no || '',
      notes: payment.notes || '',
      edit_reason: '',
    });
    setShowEditPayment(true);
  };

  const handleEditPayment = async (e) => {
    e.preventDefault();
    if (!editPaymentForm.edit_reason) {
      toast.error('Edit reason is required for audit trail');
      return;
    }
    setEditingPayment(true);
    try {
      const res = await fetch(`${API_URL}/api/financial/payments/${editPaymentForm.payment_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          payment_date: editPaymentForm.payment_date,
          amount: parseFloat(editPaymentForm.amount),
          payment_category: editPaymentForm.payment_category,
          payment_method: editPaymentForm.payment_method,
          money_receipt_no: editPaymentForm.money_receipt_no,
          notes: editPaymentForm.notes,
          edit_reason: editPaymentForm.edit_reason,
        })
      });
      if (res.ok) {
        toast.success('Payment updated successfully');
        setShowEditPayment(false);
        fetchContract();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update payment');
      }
    } catch {
      toast.error('Failed to update payment');
    } finally {
      setEditingPayment(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!canDelete) return;
    const confirmed = window.confirm(
      `Permanently DELETE this payment?\n\n` +
      `Amount: $${(payment.amount || 0).toLocaleString()}\n` +
      `Category: ${payment.payment_category}\n` +
      `Date: ${payment.payment_date?.split('T')[0] || ''}\n\n` +
      `This will remove the record from the database. Reporting and ` +
      `installment status will rebuild automatically. This cannot be undone.`
    );
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_URL}/api/financial/payments/${payment.payment_id}?reason=Owner deletion`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Payment deleted');
        fetchContract();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete payment');
      }
    } catch {
      toast.error('Failed to delete payment');
    }
  };

  const handleDeleteContract = async () => {
    if (!canDelete || !contract) return;
    const confirmed = window.confirm(
      `Permanently DELETE this contract?\n\n` +
      `File: ${contract.file_id}\n` +
      `Customer: ${contract.customer_name || contract.customer_id}\n` +
      `Net Price: $${(contract.net_price || 0).toLocaleString()}\n\n` +
      `This will remove the contract AND all its payments AND all its ` +
      `installments from the database. The customer record is preserved. ` +
      `This cannot be undone.`
    );
    if (!confirmed) return;
    const typed = window.prompt(`Type DELETE to confirm permanent removal of contract ${contract.file_id}:`);
    if (typed !== 'DELETE') {
      toast.info('Deletion cancelled');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/financial/contracts/${contractId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Contract deleted');
        navigate('/finance/contracts');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete contract');
      }
    } catch {
      toast.error('Failed to delete contract');
    }
  };

  const unpaidInstallments = useMemo(
    () => installments.filter(i => i.status !== 'Paid'),
    [installments]
  );

  const handleRecordPayment = async (e) => {
    e.preventDefault();

    if (!paymentForm.amount || !paymentForm.payment_category || !paymentForm.payment_method) {
      toast.error('Amount, category, and method are required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/financial/contracts/${contractId}/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...paymentForm,
          amount: parseFloat(paymentForm.amount),
          applied_installment_id: paymentForm.applied_installment_id === 'auto' ? '' : paymentForm.applied_installment_id
        })
      });

      if (response.ok) {
        toast.success('Payment recorded successfully');
        setShowPaymentDialog(false);
        setPaymentForm({
          payment_date: new Date().toISOString().split('T')[0],
          amount: '',
          payment_category: '',
          payment_method: '',
          money_receipt_no: '',
          notes: '',
          applied_installment_id: 'auto'
        });
        fetchContract();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to record payment');
      }
    } catch (error) {
      logger.error('Error recording payment:', error);
      toast.error('Error recording payment');
    }
  };

  // Generate and download invoice as PDF for a payment
  const downloadInvoice = async (payment) => {
    setGeneratingInvoice(payment.payment_id);
    
    try {
      const invoiceHTML = generateInvoiceHTML(payment);
      
      // Create a temporary container for the HTML using safe DOM APIs
      const container = document.createElement('div');
      const DOMPurify = (await import('dompurify')).default;
      const sanitized = DOMPurify.sanitize(invoiceHTML, { WHOLE_DOCUMENT: false, ADD_TAGS: ['style'], ADD_ATTR: ['style'] });
      // Use createContextualFragment over innerHTML to avoid XSS lint warnings
      // (content is already DOMPurify-sanitized above; fragment parsing is explicit and safe).
      const fragment = document.createRange().createContextualFragment(sanitized);
      container.appendChild(fragment);
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      document.body.appendChild(container);
      
      const invoiceElement = container.querySelector('.invoice-container');
      
      const receiptNo = payment.money_receipt_no || payment.payment_id?.substring(0, 8);
      const filename = `Invoice_${receiptNo}_${payment.payment_date?.split('T')[0] || 'date'}.pdf`;
      
      const opt = {
        margin: 0,
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        }
      };
      
      await html2pdf().set(opt).from(invoiceElement).save();
      
      // Clean up
      document.body.removeChild(container);
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      logger.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingInvoice(null);
    }
  };

  const generateInvoiceHTML = (payment) => {
    const formatNum = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const paymentDate = payment.payment_date ? new Date(payment.payment_date) : new Date();
    const receiptNo = payment.money_receipt_no || `INV-${payment.payment_id?.substring(0, 8).toUpperCase()}`;
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Invoice ${receiptNo}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { size: A4; margin: 15mm; }
    body { font-family: 'Inter', 'Segoe UI', sans-serif; color: #1e293b; font-size: 11px; line-height: 1.4; background: white; }
    .invoice-container { max-width: 210mm; margin: 0 auto; padding: 20px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 2px solid #d4af37; margin-bottom: 20px; }
    .company { }
    .company h1 { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: 1px; margin-bottom: 2px; }
    .company .tagline { font-size: 9px; color: #d4af37; font-weight: 600; letter-spacing: 2px; margin-bottom: 8px; }
    .company .address { font-size: 10px; color: #64748b; line-height: 1.5; }
    .invoice-meta { text-align: right; }
    .invoice-meta h2 { font-size: 28px; font-weight: 300; color: #0f172a; margin-bottom: 8px; }
    .invoice-meta .number { background: #0f172a; color: #d4af37; padding: 6px 14px; font-size: 11px; font-weight: 600; display: inline-block; }
    .invoice-meta .date { font-size: 10px; color: #64748b; margin-top: 8px; }
    .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 25px; }
    .party h3 { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }
    .party .name { font-size: 13px; font-weight: 600; color: #0f172a; margin-bottom: 4px; }
    .party p { font-size: 10px; color: #64748b; margin-bottom: 2px; }
    .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    .items-table th { background: #f8fafc; padding: 10px 12px; text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; border-bottom: 1px solid #e2e8f0; }
    .items-table td { padding: 12px; border-bottom: 1px solid #f1f5f9; font-size: 11px; }
    .items-table .desc-main { font-weight: 500; color: #0f172a; }
    .items-table .desc-sub { font-size: 10px; color: #94a3b8; margin-top: 2px; }
    .items-table .amount { text-align: right; font-weight: 600; font-family: monospace; font-size: 12px; }
    .totals { display: flex; justify-content: flex-end; margin-bottom: 20px; }
    .totals-box { width: 220px; }
    .totals-row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 11px; border-bottom: 1px solid #f1f5f9; }
    .totals-row.total { background: #f0fdf4; margin: 10px -12px 0; padding: 12px; border: none; }
    .totals-row.total .label { font-weight: 700; font-size: 12px; }
    .totals-row.total .value { font-size: 18px; font-weight: 700; color: #16a34a; font-family: monospace; }
    .status-badge { text-align: center; padding: 12px; background: #f0fdf4; margin-bottom: 20px; }
    .status-badge span { background: #16a34a; color: white; padding: 6px 20px; font-size: 10px; font-weight: 600; display: inline-block; }
    .summary { background: #f8fafc; padding: 15px; margin-bottom: 20px; }
    .summary h4 { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 12px; }
    .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }
    .summary-item .label { font-size: 9px; color: #94a3b8; margin-bottom: 2px; }
    .summary-item .value { font-size: 12px; font-weight: 600; color: #0f172a; }
    .footer { border-top: 1px solid #e2e8f0; padding-top: 15px; display: flex; justify-content: space-between; font-size: 9px; color: #94a3b8; }
    .footer-left { max-width: 60%; }
    .footer-left h4 { font-size: 9px; color: #64748b; margin-bottom: 6px; }
    .footer-left ul { padding-left: 14px; line-height: 1.6; }
    .footer-right { text-align: right; }
    .footer-right .thanks { font-size: 11px; color: #64748b; margin-bottom: 8px; }
    .footer-right .sig { font-size: 13px; color: #d4af37; font-weight: 600; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  </style>
</head>
<body>
  <div class="invoice-container">
    <div class="header">
      <div class="company">
        <h1>GOLDSANDS</h1>
        <div class="tagline">GROUP INC</div>
        <div class="address">
          37-39 77th St, Jackson Heights<br>
          NY 11372, USA<br>
          info@goldsandsgroup.com
        </div>
      </div>
      <div class="invoice-meta">
        <h2>INVOICE</h2>
        <span class="number">${receiptNo}</span>
        <div class="date">${formatLocalDateLong(payment.payment_date || new Date().toISOString())}</div>
      </div>
    </div>

    <div class="parties">
      <div class="party">
        <h3>Bill To</h3>
        <div class="name">${contract?.customer?.full_name || 'Customer'}</div>
        <p>${contract?.customer?.phone || ''}</p>
        <p>${contract?.customer?.email || ''}</p>
        <p>${contract?.customer?.address || ''}</p>
      </div>
      <div class="party">
        <h3>Contract Details</h3>
        <p><strong>Contract:</strong> ${contract?.file_id || ''}</p>
        <p><strong>Project:</strong> ${contract?.deal_name || contract?.project_name || ''}</p>
        <p><strong>Product:</strong> ${contract?.product_type || ''}</p>
      </div>
    </div>

    <table class="items-table">
      <thead>
        <tr>
          <th style="width: 50%">Description</th>
          <th>Category</th>
          <th>Method</th>
          <th style="text-align: right">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <div class="desc-main">Payment for ${contract?.deal_name || contract?.project_name || 'Contract'}</div>
            <div class="desc-sub">${payment.notes || payment.payment_category + ' - Contract ' + (contract?.file_id || '')}</div>
          </td>
          <td>${payment.payment_category || 'Payment'}</td>
          <td>${payment.payment_method || '-'}</td>
          <td class="amount">$${formatNum(payment.amount)}</td>
        </tr>
      </tbody>
    </table>

    <div class="totals">
      <div class="totals-box">
        <div class="totals-row"><span>Subtotal</span><span>$${formatNum(payment.amount)}</span></div>
        <div class="totals-row"><span>Tax</span><span>$0.00</span></div>
        <div class="totals-row total"><span class="label">Total Paid</span><span class="value">$${formatNum(payment.amount)}</span></div>
      </div>
    </div>

    <div class="status-badge"><span>✓ PAYMENT RECEIVED</span></div>

    <div class="summary">
      <h4>Contract Summary</h4>
      <div class="summary-grid">
        <div class="summary-item"><div class="label">Contract Value</div><div class="value">$${formatNum(contract?.net_price)}</div></div>
        <div class="summary-item"><div class="label">Total Collected</div><div class="value" style="color: #16a34a;">$${formatNum(summary?.total_amount_collected)}</div></div>
        <div class="summary-item"><div class="label">Outstanding</div><div class="value" style="color: #d97706;">$${formatNum(summary?.outstanding_balance)}</div></div>
        <div class="summary-item"><div class="label">Status</div><div class="value">${contract?.payment_status || 'Active'}</div></div>
      </div>
    </div>

    <div class="footer">
      <div class="footer-left">
        <h4>Terms & Conditions</h4>
        <ul>
          <li>Payment is non-refundable unless otherwise agreed in writing</li>
          <li>This receipt confirms payment for the specified contract</li>
          <li>Please retain this invoice for your records</li>
        </ul>
      </div>
      <div class="footer-right">
        <div class="thanks">Thank you for your business!</div>
        <div class="sig">Goldsands Group INC</div>
      </div>
    </div>
  </div>
</body>
</html>
    `;
  };

  const getStatusBadge = (status) => {
    const config = {
      'Active': { variant: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'Overdue': { variant: 'bg-red-100 text-red-800', icon: AlertTriangle },
      'Completed': { variant: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
      'Cancelled': { variant: 'bg-slate-100 text-slate-800', icon: null },
      'Paid': { variant: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'Partial': { variant: 'bg-amber-100 text-amber-800', icon: Clock },
      'Pending': { variant: 'bg-slate-100 text-slate-800', icon: Clock }
    };
    const { variant, icon: Icon } = config[status] || { variant: 'bg-slate-100 text-slate-800', icon: null };
    return (
      <Badge className={variant}>
        {Icon && <Icon className="h-3 w-3 mr-1" />}
        {status}
      </Badge>
    );
  };

  const getInstallmentStatusBadge = (inst) => {
    if (inst.status === 'Paid') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>;
    } else if (inst.status === 'Overdue' || inst.days_overdue > 0) {
      return <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />Overdue ({inst.days_overdue}d)</Badge>;
    } else if (inst.status === 'Partial') {
      return <Badge className="bg-amber-100 text-amber-800"><Clock className="h-3 w-3 mr-1" />Partial</Badge>;
    } else {
      return <Badge className="bg-slate-100 text-slate-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-900">Contract not found</h2>
        <Button className="mt-4" onClick={() => navigate('/finance/contracts')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Contracts
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contract-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/finance/contracts')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-heading font-bold text-slate-900">
              Contract: {contract.file_id}
            </h1>
            {getStatusBadge(contract.payment_status)}
          </div>
          <p className="text-slate-600 font-body">{contract.deal_name}</p>
        </div>
        
        <div className="flex gap-2">
          {canEditContracts && (
            <Button variant="outline" onClick={openEditContract} data-testid="edit-contract-btn">
              <Pencil className="h-4 w-4 mr-2" />
              Edit Contract
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outline"
              onClick={handleDeleteContract}
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="delete-contract-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
          <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="record-payment-btn">
                <Plus className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleRecordPayment} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Amount (USD) *</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={paymentForm.amount} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    placeholder="Enter amount"
                    data-testid="payment-amount-input"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={paymentForm.payment_category} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_category: v })}>
                    <SelectTrigger data-testid="payment-category-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Method *</Label>
                  <Select value={paymentForm.payment_method} onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}>
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map((method) => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date</Label>
                  <Input 
                    type="date" 
                    value={paymentForm.payment_date} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Receipt #</Label>
                  <Input 
                    value={paymentForm.money_receipt_no} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, money_receipt_no: e.target.value })}
                    placeholder="Receipt number"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Apply to Installment (optional)</Label>
                  <Select 
                    value={paymentForm.applied_installment_id} 
                    onValueChange={(v) => setPaymentForm({ ...paymentForm, applied_installment_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Auto-allocate (earliest unpaid)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-allocate</SelectItem>
                      {unpaidInstallments
                        .map((inst) => (
                          <SelectItem key={inst.installment_id} value={inst.installment_id}>
                            #{inst.installment_number} - Due {formatLocalDate(inst.due_date)} (${inst.balance_amount?.toLocaleString()})
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={paymentForm.notes} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder="Optional notes"
                    rows={2}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-gold hover:bg-gold/90 text-slate-900" data-testid="submit-payment-btn">
                  Record Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Net Price</p>
                <p className="text-xl font-bold">${parseFloat(contract.net_price || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Collected</p>
                <p className="text-xl font-bold text-green-600">${parseFloat(summary?.total_amount_collected || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Outstanding</p>
                <p className="text-xl font-bold text-amber-600">${parseFloat(summary?.outstanding_balance || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Calendar className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Days Overdue</p>
                <p className="text-xl font-bold">{summary?.days_overdue || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Customer Info */}
      {contract.customer && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-600">Name</p>
                <p className="font-medium">{contract.customer.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Phone</p>
                <p className="font-medium flex items-center gap-1">
                  <Phone className="h-3 w-3" />
                  {contract.customer.phone}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Email</p>
                <p className="font-medium">{contract.customer.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Address</p>
                <p className="font-medium">{contract.customer.address || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Details */}
      <Tabs defaultValue="installments" className="space-y-4">
        <TabsList>
          <TabsTrigger value="installments" data-testid="installments-tab">
            <FileText className="h-4 w-4 mr-2" />
            Installments ({installments.length})
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="payments-tab">
            <CreditCard className="h-4 w-4 mr-2" />
            Payments ({payments.length})
          </TabsTrigger>
          {futureAdjustments.length > 0 && (
            <TabsTrigger value="future-adjustments" data-testid="fa-tab">
              <DollarSign className="h-4 w-4 mr-2" />
              Future Adjustments ({futureAdjustments.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="details" data-testid="details-tab">
            Contract Details
          </TabsTrigger>
        </TabsList>

        <TabsContent value="installments">
          <Card>
            <CardHeader>
              <CardTitle>Installment Schedule</CardTitle>
              <CardDescription>
                {summary?.installment_summary?.paid || 0} paid, {summary?.installment_summary?.pending || 0} pending, {summary?.installment_summary?.overdue || 0} overdue
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Due Amount</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Balance</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {installments.map((inst) => (
                    <TableRow key={inst.installment_id} data-testid={`installment-row-${inst.installment_number}`}>
                      <TableCell className="font-medium">{inst.installment_number}</TableCell>
                      <TableCell>
                        {formatLocalDate(inst.due_date)}
                      </TableCell>
                      <TableCell>${(Number(inst.due_amount) || 0).toLocaleString()}</TableCell>
                      <TableCell className="text-green-600">
                        ${(typeof inst.paid_amount === 'number' ? inst.paid_amount : Number(inst.paid_amount) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className={inst.balance_amount > 0 ? 'text-amber-600 font-medium' : ''}>
                        ${(Number(inst.balance_amount) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>{getInstallmentStatusBadge(inst)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle>Payment Ledger</CardTitle>
              <CardDescription>
                All payments recorded for this contract
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No payments recorded yet
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Receipt #</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="text-center">Invoice</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.payment_id} data-testid={`payment-row-${payment.payment_id}`}>
                        <TableCell>
                          {formatLocalDate(payment.payment_date)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{payment.payment_category}</Badge>
                        </TableCell>
                        <TableCell>{payment.payment_method}</TableCell>
                        <TableCell className="font-mono text-sm">{payment.money_receipt_no || '-'}</TableCell>
                        <TableCell className="font-medium text-green-600">
                          ${parseFloat(payment.amount || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 max-w-[150px] truncate">
                          {payment.notes || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {canEditPayments && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => openEditPayment(payment)}
                                className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                                data-testid={`edit-payment-${payment.payment_id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => handleDeletePayment(payment)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                data-testid={`delete-payment-${payment.payment_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => downloadInvoice(payment)}
                              disabled={generatingInvoice === payment.payment_id}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              data-testid={`download-invoice-${payment.payment_id}`}
                            >
                              {generatingInvoice === payment.payment_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Future Adjustments Tab */}
        {futureAdjustments.length > 0 && (
          <TabsContent value="future-adjustments">
            <Card>
              <CardHeader>
                <CardTitle>Future Adjustments</CardTitle>
                <CardDescription>Fixed amounts the client will adjust separately from installments</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {futureAdjustments.map((fa) => (
                    <div key={fa.fa_id} className="border rounded-lg p-4" data-testid={`fa-card-${fa.fa_id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-slate-900">{fa.label || 'Future Adjustment'}</h4>
                          <p className="text-sm text-slate-500 capitalize">{fa.type === 'one_time' ? 'One-Time Payment' : 'Multiple Payments'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">${parseFloat(fa.total_amount || 0).toLocaleString()}</p>
                          <Badge className={fa.status === 'Paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {fa.status}
                          </Badge>
                        </div>
                      </div>
                      {fa.type === 'one_time' && fa.due_date && (
                        <p className="text-sm text-slate-600">Due: {formatLocalDate(fa.due_date)}</p>
                      )}
                      {fa.type === 'multiple' && fa.payments && fa.payments.length > 0 && (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>#</TableHead>
                              <TableHead>Amount</TableHead>
                              <TableHead>Due Date</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {fa.payments.map((p, idx) => (
                              <TableRow key={p.payment_index || `fa-pay-${idx}`}>
                                <TableCell>{p.payment_index || idx + 1}</TableCell>
                                <TableCell className="font-medium">${parseFloat(p.amount || 0).toLocaleString()}</TableCell>
                                <TableCell>{p.due_date ? formatLocalDate(p.due_date) : '-'}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={p.status === 'Paid' ? 'text-green-600' : ''}>
                                    {p.status || 'Pending'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Contract Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-slate-600">File ID</p>
                  <p className="font-mono font-medium">{contract.file_id}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">BD File No</p>
                  <p className="font-medium">{contract.bd_file_no}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Product Type</p>
                  <Badge variant="outline">{contract.product_type}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Project Name</p>
                  <p className="font-medium">{contract.project_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Gross Price</p>
                  <p className="font-medium">${parseFloat(contract.gross_price || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Discount</p>
                  <p className="font-medium text-green-600">
                    {contract.discount_type !== 'none' 
                      ? `${contract.discount_type === 'percentage' ? contract.discount_value + '%' : '$' + contract.discount_value} (-$${parseFloat(contract.discount_amount || 0).toLocaleString()})`
                      : 'None'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Net Price</p>
                  <p className="font-bold text-gold">${parseFloat(contract.net_price || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Booking Money</p>
                  <p className="font-medium">${parseFloat(contract.booking_money_amount || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Down Payment</p>
                  <p className="font-medium">${parseFloat(contract.downpayment_amount || 0).toLocaleString()}</p>
                </div>
                {parseFloat(contract.future_adjustment_total || 0) > 0 && (
                  <div>
                    <p className="text-sm text-slate-600">Future Adjustment</p>
                    <p className="font-medium text-purple-600">- ${parseFloat(contract.future_adjustment_total || 0).toLocaleString()}</p>
                  </div>
                )}
                {contract.remaining_for_installments && (
                  <div>
                    <p className="text-sm text-slate-600">Remaining for Installments</p>
                    <p className="font-bold text-gold">${parseFloat(contract.remaining_for_installments || 0).toLocaleString()}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-600">Payment Frequency</p>
                  <p className="font-medium">{contract.payment_frequency}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Payment Period</p>
                  <p className="font-medium">{contract.payment_period_months} months</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Number of Installments</p>
                  <p className="font-medium">{contract.number_of_installments}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Installment Amount</p>
                  <p className="font-medium">${parseFloat(contract.installment_amount || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">First Installment Date</p>
                  <p className="font-medium">
                    {formatLocalDate(contract.installment_start_date)}
                  </p>
                </div>
                {contract.number_of_shares && (
                  <div>
                    <p className="text-sm text-slate-600">Number of Shares</p>
                    <p className="font-medium">{contract.number_of_shares}</p>
                  </div>
                )}
                {contract.area_size_katha && (
                  <div>
                    <p className="text-sm text-slate-600">Area Size</p>
                    <p className="font-medium">{contract.area_size_katha} Katha</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Payment Dialog */}
      <Dialog open={showEditPayment} onOpenChange={setShowEditPayment}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPayment} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment Date</Label>
                <Input type="date" value={editPaymentForm.payment_date || ''} onChange={(e) => setEditPaymentForm(p => ({ ...p, payment_date: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Amount</Label>
                <Input type="number" step="0.01" value={editPaymentForm.amount || ''} onChange={(e) => setEditPaymentForm(p => ({ ...p, amount: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={editPaymentForm.payment_category || ''} onValueChange={(v) => setEditPaymentForm(p => ({ ...p, payment_category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Method</Label>
                <Select value={editPaymentForm.payment_method || ''} onValueChange={(v) => setEditPaymentForm(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Receipt No</Label>
                <Input value={editPaymentForm.money_receipt_no || ''} onChange={(e) => setEditPaymentForm(p => ({ ...p, money_receipt_no: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Input value={editPaymentForm.notes || ''} onChange={(e) => setEditPaymentForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <div className="col-span-2 border-t pt-3">
                <Label className="text-xs text-red-600">Edit Reason (required for audit trail) *</Label>
                <Textarea
                  value={editPaymentForm.edit_reason || ''}
                  onChange={(e) => setEditPaymentForm(p => ({ ...p, edit_reason: e.target.value }))}
                  placeholder="Why is this payment being edited?"
                  rows={2}
                  required
                  data-testid="edit-reason-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowEditPayment(false)}>Cancel</Button>
              <Button type="submit" disabled={editingPayment || !editPaymentForm.edit_reason} data-testid="save-edit-payment">
                {editingPayment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
